import type {CurrentStep, GetNextBuyflowStepResponse} from '@/lib/TypesOpenApi';
import {
  googleProxyPrefix,
  isCiamEnabled,
  isRequestedFromGoogleProxy,
} from '@gfiber/pixel-party';
import {useMemo} from 'react';
import {proxy} from 'valtio';

export const signupPrefix = '/signup';

export enum StepsPath {
  AdminWelcome = 'admin_welcome', // TODO might change this later.
  Battery = 'battery_step',
  EndBuyflow = 'end_buyflow',
  InternetPlan = 'internet_plan_step',
  NotificationPreferences = 'final_step',
  Phone = 'phone_step',
  PhoneQuestionnaire = 'phone_questionnaire_step',
  PointSelector = 'cpe_points_step',
  Preconfig = 'preconfig_step',
  Review = 'checkout_step',
  Schedule = 'appointment_scheduling_step',
  SelfInstallKit = 'sik_shipping_step',
  SignIn = 'sign_in_step',
  SignInOTP = 'sign_in_step_otp',
  Start = '',
  StaticIp = 'static_ip_step',
  Streaming = 'streaming_services_step',
  Verify = 'contact_info_step', // Also alias for payment.
  WifiSetup = 'wifi_setup_step',
}

export interface StepWidgetStore {
  content?: GetNextBuyflowStepResponse;
}

export interface Step {
  name: CurrentStep;
  path: string;
  store: StepWidgetStore;
}

/**
 * Used to generate stores and rest of the info steps
 * Order is important, first and last item are used for buttons rendering
 * */
const stepsBase: Array<Omit<Step, 'store'>> = [
  {
    name: 'START_BUYFLOW',
    path: StepsPath.Start,
  },
  {
    name: 'PRECONFIG',
    path: StepsPath.Preconfig,
  },
  {
    name: 'INTERNET',
    path: StepsPath.InternetPlan,
  },
  {
    name: 'STATIC_IP',
    path: StepsPath.StaticIp,
  },
  {
    name: 'PHONE',
    path: StepsPath.Phone,
  },
  {
    name: 'BATTERY',
    path: StepsPath.Battery,
  },
  {
    name: 'PHONE_QUESTIONNAIRE',
    path: StepsPath.PhoneQuestionnaire,
  },
  {
    name: 'SCHEDULING',
    path: StepsPath.Schedule,
  },
  {
    name: 'STREAMING',
    path: StepsPath.Streaming,
  },
  {
    name: 'SELF_INSTALL_KIT',
    path: StepsPath.SelfInstallKit,
  },
  {
    name: 'POINT_SELECTOR',
    path: StepsPath.PointSelector,
  },
  {
    name: 'REVIEW',
    path: StepsPath.Review,
  },
  {
    name: 'NOTIFICATION_PREFERENCES',
    path: StepsPath.NotificationPreferences,
  },
  {
    name: 'SIGN_IN',
    path: StepsPath.SignIn,
  },
  {
    name: 'SIGN_IN_OTP',
    path: StepsPath.SignInOTP,
  },
  {
    name: 'VERIFY',
    path: StepsPath.Verify,
  },
  {
    name: 'WIFI_SETUP',
    path: StepsPath.WifiSetup,
  },
  {
    name: 'BILLING',
    path: StepsPath.Verify,
  },
  {
    name: 'END_BUYFLOW',
    path: StepsPath.EndBuyflow,
  },
];

/**
 * Full list of pages with their content stores.
 */
export const stepsList: Array<Step> = stepsBase.map(item => ({
  ...item,
  // generate store which can be prefiled before page load
  store: proxy<StepWidgetStore>({content: undefined}),
}));

/** Map used to route back-end step id to front-end routing path */
export interface StepMap {
  /** Actually name is CurrentStep, but it generated incorrectly by OpenAPI */
  [name: string]: Step;
}

export const stepsMap: StepMap = stepsList.reduce(
  (map, item) => ({
    ...map,
    [item.name]: item,
  }),
  {} as StepMap,
);

// Prefill steps map with start step
// To allow subscribe for store before data is loaded
stepsMap.START_BUYFLOW.store.content = undefined;

export const SHOW_CART_ON_PAGES = [
  StepsPath.Phone,
  StepsPath.Battery,
  StepsPath.PhoneQuestionnaire,
  StepsPath.SelfInstallKit,
  StepsPath.PointSelector,
  StepsPath.Schedule,
  StepsPath.Streaming,
  StepsPath.StaticIp,
  StepsPath.Verify,
  StepsPath.SignIn,
];

export const getPagePrefix = () =>
  isRequestedFromGoogleProxy()
    ? `${googleProxyPrefix}${signupPrefix}`
    : signupPrefix;

/**
 * Get the real step path from request pathname for
 * request coming from both Google-GFiber Proxy, or GFiber directly.
 *
 * @remarks
 * The path passed in is only get from usePathname and under signup route.
 * The request path should never be '', '/', 'other-than-signup', '/other-than-signup'.
 * It should only start with '/signup' without trailing  '/'.
 * Valid cases: '/signup', '/signup/step-path', '/a/signup', '/a/signup/step-path' etc.
 * '/a' is the Google-GFiber proxy prefix.
 * Invalid cases can be ignored: '', 'a', 'a/', '/', 'a/b', 'a/b/',
 * 'signup', 'signup/', 'a/signup', 'a/singup/' etc.
 *
 * This function should be the only one to use for getting step path from request pathname.
 *
 * @param path - The path before transform
 * @returns The real step path
 */
export const getStepPathFromRequestPath = (path: string) => {
  const prefix = getPagePrefix();
  return path.replace(prefix, '').split('/').at(1) || '';
};

export function showCartOnCurrentPage(pathName: string): boolean {
  return SHOW_CART_ON_PAGES.includes(
    getStepPathFromRequestPath(pathName) as StepsPath,
  );
}

export const HIDE_FOOTER_ON_PAGES = [
  StepsPath.Start,
  isCiamEnabled() && StepsPath.SignIn,
];

export function hideFooterOnCurrentPage(pathName: string): boolean {
  return HIDE_FOOTER_ON_PAGES.includes(
    getStepPathFromRequestPath(pathName) as StepsPath,
  );
}

export const NOT_ALLOWED_BACK_PAGES: StepsPath[] = [
  StepsPath.WifiSetup,
  StepsPath.NotificationPreferences,
];

/**
 * To check if current step will allow to go back or not.
 *
 * @remarks
 * Index should start from 1 since we always add the first step in the history.
 * Our first step /signup will always call nextStep automatically
 * go to the really first step, which will varies depend on BFF return.
 *
 * @param currentStep - current step
 * @param pages - pages in the history
 * @returns true allow to go back, false not
 */
export const allowGoBackFromCurrentStep = (
  currentStep: StepsPath | undefined,
  pages: readonly StepsPath[],
) => {
  const currentStepPath = (currentStep || '') as StepsPath;
  const currentPageIndex = pages.indexOf(currentStepPath);
  return (
    currentPageIndex > 1 && !NOT_ALLOWED_BACK_PAGES.includes(currentStepPath)
  );
};

export const SKIPPABLE_PREV_PAGES = [StepsPath.SignIn];

export const isPrevPageSkippable = (path: StepsPath) =>
  SKIPPABLE_PREV_PAGES.includes(path);

/**
 * Looking for previous page path. Move back until page isn't skippable or prohibited for 'step back'
 *
 * @param stepPath currentStep
 * @returns [current index of the path or -1 if not allowed to go back, number of steps to go back]
 */
export function getPrevIndexAndSteps(
  stepPath: StepsPath,
  pages: readonly StepsPath[],
): [number, number] {
  const index = pages.indexOf(stepPath);
  if (!allowGoBackFromCurrentStep(stepPath as StepsPath, pages)) {
    console.error(
      'Routing error: unexpected situation, current page does not have previous page',
    );
    return [-1, 0];
  }
  const page = pages[index - 1];

  if (isPrevPageSkippable(page)) {
    const [newIndex, newCount] = getPrevIndexAndSteps(page, pages);
    return [newIndex, newCount + 1];
  }

  return [index, 1];
}

export const useIsBackButtonShown = (
  currentStep: Step | undefined,
  pages: readonly StepsPath[],
) => {
  return useMemo(() => {
    return allowGoBackFromCurrentStep(currentStep?.path as StepsPath, pages);
  }, [currentStep, pages]);
};

export const AUTH_REQUIRED_PAGES = [
  StepsPath.Verify, // Also used for payment.
  StepsPath.NotificationPreferences,
  StepsPath.WifiSetup,
  StepsPath.EndBuyflow,
];

export const buildRedirectUrlTo = (path: StepsPath) => {
  const url = new URL(window.location.href);

  url.pathname = url.pathname.split('/').slice(0, -1).concat([path]).join('/');

  return url.toString();
};
